import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, DescriptionList, FormRow, LinkButton, Modal, ModalFooter, Notice, Row, Spinner, TextAreaField, TextField, useToast } from "@plataforma/design-system";
import { formatCpf, type Invoice } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { ApiError, errorMessage } from "../../lib/api";
import { todayIso, whatsappUrl } from "../../lib/format";
import { invoiceStatusLabel, invoiceStatusTone } from "../../lib/labels";
import { certificateMessage, useCancelInvoice, useFiscalSettings, useIssueInvoice, useRegisterManual, useReissueInvoice } from "./api";
import "./invoices.css";

export type InvoiceStep = "emit" | "view" | "manual" | "cancel";

interface FlowProps {
  invoice: Invoice;
  step: InvoiceStep;
  /** Próxima nota pendente, para emendar a fila do dia depois de uma emissão. */
  nextPending?: Invoice;
  pendingCount?: number;
  onStep: (invoice: Invoice, step: InvoiceStep, justIssued?: boolean) => void;
  onClose: () => void;
  justIssued?: boolean;
}

/**
 * Jornada da nota fiscal num único lugar: conferir e emitir, ver, registrar manualmente e cancelar.
 * Usada pela tela de notas e pela agenda, para a emissão estar onde a consulta termina.
 */
export function InvoiceFlow(props: FlowProps) {
  if (props.step === "emit") return <EmitStep {...props} />;
  if (props.step === "manual") return <ManualStep {...props} />;
  if (props.step === "cancel") return <CancelStep {...props} />;
  return <ViewStep {...props} />;
}

function EmitStep({ invoice, onStep, onClose }: FlowProps) {
  const { format } = useAppConfig();
  const navigate = useNavigate();
  const fiscal = useFiscalSettings();
  const issue = useIssueInvoice(invoice.id);
  const [values, setValues] = useState({ cpf: formatCpf(invoice.clientCpf), value: String(invoice.value), description: invoice.description });
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const set = (field: keyof typeof values) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
    setDirty(true);
  };
  const certificate = certificateMessage(fiscal.data, format.date);
  const manualLink = <Button variant="quiet" onClick={() => onStep(invoice, "manual")}>Já emiti fora do sistema</Button>;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (values.cpf.replace(/\D/g, "").length !== 11) return setError("Informe o CPF do tomador com 11 dígitos.");
    if (!(Number(values.value) > 0)) return setError("Informe um valor maior que zero.");
    if (!values.description.trim()) return setError("Descreva o serviço prestado.");
    try {
      const issued = await issue.mutateAsync({ cpf: values.cpf, value: Number(values.value), description: values.description });
      onStep(issued, "view", true);
    } catch (problem) {
      // Falha do emissor vira uma tela própria; os dados conferidos continuam no formulário para tentar de novo.
      if (problem instanceof ApiError && (problem.code === "ISSUER_FAILURE" || problem.code === "INVOICE_BUSY")) setFailure(problem.message);
      else setError(errorMessage(problem));
    }
  }

  if (certificate?.blocking) {
    return (
      <Modal title="Emitir nota fiscal" onClose={onClose}>
        <Result tone="fail" title="Falta o certificado digital">{certificate.text}</Result>
        <ModalFooter start={manualLink}>
          <Button onClick={onClose}>Fechar</Button>
          <Button variant="primary" onClick={() => { onClose(); navigate("/configuracoes"); }}>Abrir Configurações</Button>
        </ModalFooter>
      </Modal>
    );
  }
  if (issue.isPending) {
    return (
      <Modal title="Emitir nota fiscal" onClose={() => undefined}>
        <div className="invoice-progress"><Spinner label="Transmitindo" /><div>Transmitindo ao emissor…</div></div>
      </Modal>
    );
  }
  if (failure) {
    return (
      <Modal title="Emitir nota fiscal" onClose={onClose}>
        <Result tone="fail" title="A nota não foi emitida">{failure} Nenhuma nota foi gerada. A pendência continua na lista, com os dados que você conferiu.</Result>
        <ModalFooter start={manualLink}>
          <Button onClick={onClose}>Fechar</Button>
          <Button variant="primary" onClick={() => setFailure(null)}>Tentar de novo</Button>
        </ModalFooter>
      </Modal>
    );
  }
  return (
    <Modal title="Emitir nota fiscal" onClose={onClose} dirty={dirty} wide>
      <form onSubmit={submit} noValidate>
        {invoice.lastError ? <div style={{ marginBottom: 14 }}><Notice tone="danger"><strong>A última tentativa falhou.</strong> {invoice.lastError}</Notice></div> : null}
        <DescriptionList items={[
          { term: "Tomador", value: invoice.clientName },
          { term: "Consulta", value: `${invoice.appointmentType} · ${format.date(invoice.appointmentDate)}` },
          { term: "Prestador", value: fiscal.data?.providerName || "a configurar" },
          { term: "Código de serviço", value: fiscal.data?.serviceCode || "a configurar" },
        ]} />
        <FormRow>
          <TextField label="CPF do tomador" inputMode="numeric" autoComplete="off" placeholder="000.000.000-00" value={values.cpf} onChange={set("cpf")} hint={invoice.clientCpf ? "Do cadastro." : "Ainda não está no cadastro. Fica salvo para as próximas notas."} />
          <TextField label="Valor da nota" type="number" min="0.01" step="0.01" inputMode="decimal" value={values.value} onChange={set("value")} hint="Valor combinado na consulta." />
        </FormRow>
        <TextAreaField label="Descrição do serviço" maxLength={300} value={values.description} onChange={set("description")} />
        {error ? <p className="ds-field__error" role="alert">{error}</p> : null}
        <ModalFooter start={manualLink}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary">Emitir nota</Button>
        </ModalFooter>
        <p className="ds-muted ds-small" style={{ marginTop: 10 }}>Confira os dados: depois de emitida, a nota só pode ser cancelada.{fiscal.data?.issuer === "Simulado" ? " Emissor simulado: nada é transmitido ao governo." : ""}</p>
      </form>
    </Modal>
  );
}

function ViewStep({ invoice, justIssued, nextPending, pendingCount, onStep, onClose }: FlowProps) {
  const { format } = useAppConfig();
  const toast = useToast();
  const reissue = useReissueInvoice(invoice.id);
  const shareText = `Olá, ${invoice.clientName.split(" ")[0]}! Segue a nota fiscal ${invoice.number} da consulta de ${format.date(invoice.appointmentDate)}, no valor de ${format.money(invoice.value)}.`;
  const cancelled = invoice.status === "cancelled";
  async function replace() {
    try {
      onStep(await reissue.mutateAsync(), "emit");
    } catch (failure) {
      toast(errorMessage(failure));
    }
  }
  return (
    <Modal title={justIssued ? "Nota emitida" : "Nota fiscal"} onClose={onClose} wide>
      {justIssued ? <Result tone="ok" title={`Nota ${invoice.number} emitida`}>{invoice.clientName} · {format.money(invoice.value)}</Result> : null}
      <DescriptionList items={[
        { term: "Número", value: invoice.number ?? "—" },
        { term: "Situação", value: <Badge tone={invoiceStatusTone[invoice.status]}>{invoiceStatusLabel[invoice.status]}</Badge> },
        { term: "Tomador", value: <>{invoice.clientName}{invoice.clientCpf ? <div className="ds-muted ds-small">CPF {formatCpf(invoice.clientCpf)}</div> : null}</> },
        { term: "Valor", value: format.money(invoice.value) },
        { term: "Emissão", value: `${invoice.issuedAt ? format.date(invoice.issuedAt) : "—"}${invoice.manual ? " · registrada manualmente" : invoice.issuedByName ? ` · por ${invoice.issuedByName}` : ""}` },
        { term: "Consulta", value: `${invoice.appointmentType} · ${format.date(invoice.appointmentDate)}` },
        { term: "Descrição do serviço", value: invoice.description, wide: true },
        ...(invoice.accessKey ? [{ term: "Chave de acesso", value: <span className="ds-mono">{invoice.accessKey}</span>, wide: true }] : []),
      ]} />
      {cancelled ? (
        <Notice>Cancelada em {invoice.cancelledAt ? format.date(invoice.cancelledAt) : "—"}. Motivo: {invoice.cancelReason}</Notice>
      ) : invoice.status === "issued" ? (
        <Row>
          {invoice.manual ? <span className="ds-muted ds-small">Nota registrada manualmente: os arquivos ficam no emissor do governo.</span> : null}
          <LinkButton size="small" href={whatsappUrl(invoice.clientPhone, shareText)} target="_blank" rel="noopener">Avisar o cliente pelo WhatsApp</LinkButton>
        </Row>
      ) : null}
      <ModalFooter start={cancelled ? (invoice.isCurrent ? <Button size="small" onClick={replace} disabled={reissue.isPending}>Emitir nota substituta</Button> : null) : invoice.status === "issued" ? <Button variant="quiet" tone="danger" onClick={() => onStep(invoice, "cancel")}>Cancelar nota</Button> : null}>
        <Button onClick={onClose}>Fechar</Button>
        {justIssued && nextPending ? <Button variant="primary" onClick={() => onStep(nextPending, "emit")}>Próxima pendente ({pendingCount}) →</Button> : null}
      </ModalFooter>
    </Modal>
  );
}

function ManualStep({ invoice, onStep, onClose }: FlowProps) {
  const { format } = useAppConfig();
  const toast = useToast();
  const register = useRegisterManual(invoice.id);
  const [values, setValues] = useState({ number: "", issuedAt: todayIso(), value: String(invoice.value || ""), cpf: formatCpf(invoice.clientCpf) });
  const [error, setError] = useState<string | null>(null);
  const set = (field: keyof typeof values) => (event: { target: { value: string } }) => setValues((current) => ({ ...current, [field]: event.target.value }));
  const copyData = async () => {
    const text = [`Nome: ${invoice.clientName}`, `CPF: ${invoice.clientCpf ? formatCpf(invoice.clientCpf) : "não informado"}`, `Valor: ${format.money(invoice.value)}`, `Data da consulta: ${format.date(invoice.appointmentDate)}`, `Descrição: ${invoice.description}`].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast("Dados copiados para colar no emissor.");
    } catch {
      toast("Não foi possível copiar automaticamente.");
    }
  };
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await register.mutateAsync({ number: values.number, issuedAt: values.issuedAt, value: Number(values.value), cpf: values.cpf });
      toast("Nota registrada manualmente.");
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }
  return (
    <Modal title="Registrar nota emitida fora do sistema" onClose={onClose} dirty={values.number.length > 0}>
      <p className="ds-muted ds-small" style={{ marginBottom: 12 }}>{invoice.clientName} · consulta de {format.date(invoice.appointmentDate)}</p>
      <div style={{ marginBottom: 14 }}><Notice action={<Button size="small" onClick={copyData}>Copiar dados</Button>}>Use quando a nota já foi emitida direto no emissor do governo.</Notice></div>
      <form onSubmit={submit}>
        <FormRow>
          <TextField label="Número da nota" required autoComplete="off" value={values.number} onChange={set("number")} />
          <TextField label="Data de emissão" type="date" required value={values.issuedAt} onChange={set("issuedAt")} />
        </FormRow>
        <FormRow>
          <TextField label="Valor da nota" type="number" min="0.01" step="0.01" required inputMode="decimal" value={values.value} onChange={set("value")} />
          <TextField label="CPF (opcional)" inputMode="numeric" autoComplete="off" value={values.cpf} onChange={set("cpf")} />
        </FormRow>
        {error ? <p className="ds-field__error" role="alert">{error}</p> : null}
        <ModalFooter start={<Button variant="quiet" onClick={() => onStep(invoice, "emit")}>← Emitir pelo sistema</Button>}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={register.isPending}>Registrar nota</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function CancelStep({ invoice, onStep, onClose }: FlowProps) {
  const { format } = useAppConfig();
  const toast = useToast();
  const cancel = useCancelInvoice(invoice.id);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const cancelled = await cancel.mutateAsync(reason);
      toast(`Nota ${cancelled.number} cancelada.`);
      onStep(cancelled, "view");
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }
  return (
    <Modal title="Cancelar nota fiscal" onClose={onClose} dirty={reason.length > 0}>
      <p className="ds-muted ds-small" style={{ marginBottom: 12 }}>Nota {invoice.number} · {format.money(invoice.value)}</p>
      <form onSubmit={submit}>
        <TextAreaField label="Motivo do cancelamento" required maxLength={200} placeholder="Ex.: valor informado errado" value={reason} onChange={(event) => setReason(event.target.value)} error={error}
          hint="O cancelamento é comunicado ao emissor e não pode ser desfeito." />
        <ModalFooter>
          <Button onClick={() => onStep(invoice, "view")}>Voltar</Button>
          <Button type="submit" variant="primary" disabled={cancel.isPending}>Cancelar a nota</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function Result({ tone, title, children }: { tone: "ok" | "fail"; title: string; children: React.ReactNode }) {
  return (
    <div className={`invoice-result invoice-result--${tone}`}>
      <div className="invoice-result__mark" aria-hidden="true">{tone === "ok" ? "✓" : "!"}</div>
      <h3 className="ds-h2">{title}</h3>
      <p className="ds-muted ds-small">{children}</p>
    </div>
  );
}
